import { useState, useEffect } from 'react';
import { useDeliveryTemplates } from '@/hooks/useDeliveryTemplates';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Edit, Loader2, Plus, X } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown } from 'lucide-react';
import { useAddressValidation, StandardizedAddress } from '@/hooks/useAddressValidation';
import { AddressValidationBadge } from '@/components/shared/AddressValidationBadge';
import { AddressSuggestionDialog } from '@/components/shared/AddressSuggestionDialog';
import { Badge } from '@/components/ui/badge';
import { addressDiffersFromStandardized } from '@/utils/addressUtils';

type LocationType = 'office' | 'warehouse' | 'job_site' | 'distribution_center' | 'retail_location' | 'other';

interface AddressDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  address?: any | null;
  mode: 'view' | 'edit' | 'create';
  onModeChange: (mode: 'view' | 'edit' | 'create') => void;
}

export const AddressDialog = ({ 
  open, 
  onOpenChange, 
  address, 
  mode,
  onModeChange 
}: AddressDialogProps) => {
  const { toast } = useToast();
  const { createTemplate, updateTemplate } = useDeliveryTemplates();
  
  const [formData, setFormData] = useState({
    location_name: '',
    location_type: 'office' as LocationType,
    address_line1: '',
    address_line2: '',
    city: '',
    state_province: '',
    postal_code: '',
    country: 'USA',
    contact_name: '',
    contact_phone: '',
    contact_email: '',
    special_instructions: '',
    access_requirements: '',
    dock_information: '',
    delivery_hours: {},
    preferred_carriers: [] as string[],
    restricted_carriers: [] as string[],
  });

  const [deliveryDetailsOpen, setDeliveryDetailsOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showSuggestionDialog, setShowSuggestionDialog] = useState(false);
  const [newPreferredCarrier, setNewPreferredCarrier] = useState('');
  const [newRestrictedCarrier, setNewRestrictedCarrier] = useState('');
  
  // Address validation
  const { validateAddress, isValidating, lastResult, clearValidation } = useAddressValidation();
  const [addressValidated, setAddressValidated] = useState(false);
  const [addressDeliverability, setAddressDeliverability] = useState<string | undefined>(undefined);

  const locationTypes: LocationType[] = [
    'office',
    'warehouse',
    'job_site',
    'distribution_center',
    'retail_location',
    'other'
  ];

  const getLocationTypeLabel = (type: LocationType) => {
    const labels: Record<LocationType, string> = {
      office: 'Office',
      warehouse: 'Warehouse',
      job_site: 'Job Site',
      distribution_center: 'Distribution Center',
      retail_location: 'Retail Location',
      other: 'Other'
    };
    return labels[type];
  };

  useEffect(() => {
    if (address && (mode === 'view' || mode === 'edit')) {
      setFormData({
        location_name: address.location_name || '',
        location_type: address.location_type || 'office',
        address_line1: address.address_line1 || '',
        address_line2: address.address_line2 || '',
        city: address.city || '',
        state_province: address.state_province || '',
        postal_code: address.postal_code || '',
        country: address.country || 'USA',
        contact_name: address.contact_name || '',
        contact_phone: address.contact_phone || '',
        contact_email: address.contact_email || '',
        special_instructions: address.special_instructions || '',
        access_requirements: address.access_requirements || '',
        dock_information: address.dock_information || '',
        delivery_hours: address.delivery_hours || {},
        preferred_carriers: address.preferred_carriers || [],
        restricted_carriers: address.restricted_carriers || [],
      });
      
      // Set validation status from stored data
      setAddressValidated(address.address_validated || false);
      setAddressDeliverability(address.address_deliverability || undefined);
    } else if (mode === 'create') {
      setFormData({
        location_name: '',
        location_type: 'office',
        address_line1: '',
        address_line2: '',
        city: '',
        state_province: '',
        postal_code: '',
        country: 'USA',
        contact_name: '',
        contact_phone: '',
        contact_email: '',
        special_instructions: '',
        access_requirements: '',
        dock_information: '',
        delivery_hours: {},
        preferred_carriers: [],
        restricted_carriers: [],
      });
      setAddressValidated(false);
      setAddressDeliverability(undefined);
      clearValidation();
    }
  }, [address, mode, clearValidation]);

  const addCarrier = (carrierType: 'preferred' | 'restricted') => {
    const carrierValue = carrierType === 'preferred' ? newPreferredCarrier : newRestrictedCarrier;
    if (carrierValue.trim()) {
      const carriers = carrierType === 'preferred' 
        ? formData.preferred_carriers 
        : formData.restricted_carriers;
      
      if (!carriers.includes(carrierValue.trim())) {
        setFormData({
          ...formData,
          [carrierType === 'preferred' ? 'preferred_carriers' : 'restricted_carriers']: [
            ...carriers,
            carrierValue.trim()
          ]
        });
        if (carrierType === 'preferred') {
          setNewPreferredCarrier('');
        } else {
          setNewRestrictedCarrier('');
        }
      }
    }
  };

  const removeCarrier = (carrier: string, carrierType: 'preferred' | 'restricted') => {
    const field = carrierType === 'preferred' ? 'preferred_carriers' : 'restricted_carriers';
    setFormData({
      ...formData,
      [field]: formData[field].filter(c => c !== carrier)
    });
  };

  const handleSave = async () => {
    if (!formData.location_name.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Location name is required',
        variant: 'destructive',
      });
      return;
    }

    if (!formData.address_line1.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Address line 1 is required',
        variant: 'destructive',
      });
      return;
    }

    if (!formData.city.trim() || !formData.state_province.trim() || !formData.postal_code.trim()) {
      toast({
        title: 'Validation Error',
        description: 'City, State/Province, and Postal Code are required',
        variant: 'destructive',
      });
      return;
    }

    try {
      setSaving(true);
      
      // Validate address using Smarty API
      const validationResult = await validateAddress({
        street: formData.address_line1,
        city: formData.city,
        state: formData.state_province,
        zipcode: formData.postal_code,
      });
      
      // Check if standardized address differs from user input
      const addressDiffers = addressDiffersFromStandardized(
        {
          street: formData.address_line1,
          city: formData.city,
          state: formData.state_province,
          zipcode: formData.postal_code,
        },
        validationResult.standardizedAddress
      );

      const hasSuggestions = validationResult.standardizedAddress || 
        (validationResult.suggestions && validationResult.suggestions.length > 0);
      
      // Show dialog if address was corrected OR if invalid with suggestions
      if (addressDiffers || (hasSuggestions && !validationResult.isValid)) {
        setShowSuggestionDialog(true);
        setSaving(false);
        return;
      }
      
      // If invalid and no suggestions, allow save with warning
      if (!validationResult.isValid) {
        await saveAddress(false, validationResult.deliverability);
        return;
      }
      
      // Address is valid and matches standardized format
      await saveAddress(true, validationResult.deliverability);
    } catch (error) {
      console.error('Error saving address:', error);
      setSaving(false);
    }
  };

  const saveAddress = async (
    validated: boolean, 
    deliverability?: string,
    overrideData?: Partial<typeof formData>
  ) => {
    const dataToSave = {
      ...formData,
      ...overrideData, // Merge override data (takes precedence over stale formData)
      address_validated: validated,
      address_validated_at: validated ? new Date().toISOString() : null,
      address_deliverability: deliverability || null,
    };

    try {
      if (mode === 'create') {
        await createTemplate(dataToSave);
      } else if (mode === 'edit' && address) {
        await updateTemplate(address.id, dataToSave);
      }
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  const handleSelectSuggestedAddress = (suggestedAddress: StandardizedAddress) => {
    // Build the corrected address fields
    const updatedAddressFields = {
      address_line1: suggestedAddress.street,
      address_line2: suggestedAddress.street2 || '',
      city: suggestedAddress.city,
      state_province: suggestedAddress.state,
      postal_code: suggestedAddress.plus4 
        ? `${suggestedAddress.zipcode}-${suggestedAddress.plus4}` 
        : suggestedAddress.zipcode,
    };
    
    // Update state for UI consistency
    setFormData(prev => ({
      ...prev,
      ...updatedAddressFields,
    }));
    setAddressValidated(true);
    setAddressDeliverability(lastResult?.deliverability);
    
    // Pass the updated fields directly to saveAddress to avoid stale state
    saveAddress(true, lastResult?.deliverability, updatedAddressFields);
  };

  const handleKeepOriginal = () => {
    // Save without validation
    saveAddress(false, 'unknown');
  };

  const handleEditAddress = () => {
    // Just close the suggestion dialog, don't save - let user edit
    setShowSuggestionDialog(false);
  };

  const handleManualValidation = async () => {
    const result = await validateAddress({
      street: formData.address_line1,
      city: formData.city,
      state: formData.state_province,
      zipcode: formData.postal_code,
    });
    
    if (result.isValid) {
      setAddressValidated(true);
      setAddressDeliverability(result.deliverability);
    }
  };

  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit';
  const isCreateMode = mode === 'create';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isViewMode && 'View Address'}
            {isEditMode && 'Edit Address'}
            {isCreateMode && 'Create New Address'}
          </DialogTitle>
          <DialogDescription>
            {isViewMode && 'Address details'}
            {isEditMode && 'Update address information'}
            {isCreateMode && 'Create a reusable delivery location'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Basic Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="location-name">
                  Location Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="location-name"
                  value={formData.location_name}
                  onChange={(e) => setFormData({ ...formData, location_name: e.target.value })}
                  placeholder="e.g., Main Warehouse, Downtown Office"
                  disabled={isViewMode}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="location-type">
                  Location Type <span className="text-destructive">*</span>
                </Label>
                <Select
                  value={formData.location_type}
                  onValueChange={(v) => setFormData({ ...formData, location_type: v as LocationType })}
                  disabled={isViewMode}
                >
                  <SelectTrigger id="location-type">
                    <SelectValue placeholder="Select location type" />
                  </SelectTrigger>
                  <SelectContent>
                    {locationTypes.map((type) => (
                      <SelectItem key={type} value={type}>
                        {getLocationTypeLabel(type)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Address Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Address Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="address-line1">
                  Address Line 1 <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="address-line1"
                  value={formData.address_line1}
                  onChange={(e) => setFormData({ ...formData, address_line1: e.target.value })}
                  placeholder="Street address"
                  disabled={isViewMode}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="address-line2">Address Line 2</Label>
                <Input
                  id="address-line2"
                  value={formData.address_line2}
                  onChange={(e) => setFormData({ ...formData, address_line2: e.target.value })}
                  placeholder="Apartment, suite, building, floor, etc."
                  disabled={isViewMode}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="city">
                    City <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder="City"
                    disabled={isViewMode}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="state">
                    State/Province <span className="text-destructive">*</span>
                  </Label>
                  <Select
                    value={formData.state_province}
                    onValueChange={(v) => setFormData({ ...formData, state_province: v })}
                    disabled={isViewMode}
                  >
                    <SelectTrigger id="state">
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      <SelectItem value="AL">Alabama (AL)</SelectItem>
                      <SelectItem value="AK">Alaska (AK)</SelectItem>
                      <SelectItem value="AZ">Arizona (AZ)</SelectItem>
                      <SelectItem value="AR">Arkansas (AR)</SelectItem>
                      <SelectItem value="CA">California (CA)</SelectItem>
                      <SelectItem value="CO">Colorado (CO)</SelectItem>
                      <SelectItem value="CT">Connecticut (CT)</SelectItem>
                      <SelectItem value="DE">Delaware (DE)</SelectItem>
                      <SelectItem value="FL">Florida (FL)</SelectItem>
                      <SelectItem value="GA">Georgia (GA)</SelectItem>
                      <SelectItem value="HI">Hawaii (HI)</SelectItem>
                      <SelectItem value="ID">Idaho (ID)</SelectItem>
                      <SelectItem value="IL">Illinois (IL)</SelectItem>
                      <SelectItem value="IN">Indiana (IN)</SelectItem>
                      <SelectItem value="IA">Iowa (IA)</SelectItem>
                      <SelectItem value="KS">Kansas (KS)</SelectItem>
                      <SelectItem value="KY">Kentucky (KY)</SelectItem>
                      <SelectItem value="LA">Louisiana (LA)</SelectItem>
                      <SelectItem value="ME">Maine (ME)</SelectItem>
                      <SelectItem value="MD">Maryland (MD)</SelectItem>
                      <SelectItem value="MA">Massachusetts (MA)</SelectItem>
                      <SelectItem value="MI">Michigan (MI)</SelectItem>
                      <SelectItem value="MN">Minnesota (MN)</SelectItem>
                      <SelectItem value="MS">Mississippi (MS)</SelectItem>
                      <SelectItem value="MO">Missouri (MO)</SelectItem>
                      <SelectItem value="MT">Montana (MT)</SelectItem>
                      <SelectItem value="NE">Nebraska (NE)</SelectItem>
                      <SelectItem value="NV">Nevada (NV)</SelectItem>
                      <SelectItem value="NH">New Hampshire (NH)</SelectItem>
                      <SelectItem value="NJ">New Jersey (NJ)</SelectItem>
                      <SelectItem value="NM">New Mexico (NM)</SelectItem>
                      <SelectItem value="NY">New York (NY)</SelectItem>
                      <SelectItem value="NC">North Carolina (NC)</SelectItem>
                      <SelectItem value="ND">North Dakota (ND)</SelectItem>
                      <SelectItem value="OH">Ohio (OH)</SelectItem>
                      <SelectItem value="OK">Oklahoma (OK)</SelectItem>
                      <SelectItem value="OR">Oregon (OR)</SelectItem>
                      <SelectItem value="PA">Pennsylvania (PA)</SelectItem>
                      <SelectItem value="RI">Rhode Island (RI)</SelectItem>
                      <SelectItem value="SC">South Carolina (SC)</SelectItem>
                      <SelectItem value="SD">South Dakota (SD)</SelectItem>
                      <SelectItem value="TN">Tennessee (TN)</SelectItem>
                      <SelectItem value="TX">Texas (TX)</SelectItem>
                      <SelectItem value="UT">Utah (UT)</SelectItem>
                      <SelectItem value="VT">Vermont (VT)</SelectItem>
                      <SelectItem value="VA">Virginia (VA)</SelectItem>
                      <SelectItem value="WA">Washington (WA)</SelectItem>
                      <SelectItem value="WV">West Virginia (WV)</SelectItem>
                      <SelectItem value="WI">Wisconsin (WI)</SelectItem>
                      <SelectItem value="WY">Wyoming (WY)</SelectItem>
                      <SelectItem value="DC">District of Columbia (DC)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="postal">
                    Postal Code <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="postal"
                    value={formData.postal_code}
                    onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                    placeholder="Postal/ZIP code"
                    disabled={isViewMode}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="country">
                    Country <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="country"
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    placeholder="Country"
                    disabled={isViewMode}
                  />
                </div>
              </div>
              
              {/* Validation Status */}
              <div className="flex items-center gap-2 pt-2">
                <AddressValidationBadge
                  isValidated={addressValidated}
                  deliverability={addressDeliverability as any}
                  onVerify={!isViewMode ? handleManualValidation : undefined}
                  isVerifying={isValidating}
                />
              </div>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Contact Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="contact-name">Contact Name</Label>
                <Input
                  id="contact-name"
                  value={formData.contact_name}
                  onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                  placeholder="Primary contact name"
                  disabled={isViewMode}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="contact-phone">Contact Phone</Label>
                  <Input
                    id="contact-phone"
                    value={formData.contact_phone}
                    onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                    placeholder="Phone number"
                    disabled={isViewMode}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contact-email">Contact Email</Label>
                  <Input
                    id="contact-email"
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                    placeholder="Email address"
                    disabled={isViewMode}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Delivery Details (Collapsible) */}
          <Collapsible open={deliveryDetailsOpen} onOpenChange={setDeliveryDetailsOpen}>
            <Card>
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer hover:bg-secondary/50 transition-colors">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">Delivery Details</CardTitle>
                    <ChevronDown className={`h-4 w-4 transition-transform ${deliveryDetailsOpen ? 'rotate-180' : ''}`} />
                  </div>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="special-instructions">Special Instructions</Label>
                    <Textarea
                      id="special-instructions"
                      value={formData.special_instructions}
                      onChange={(e) => setFormData({ ...formData, special_instructions: e.target.value })}
                      placeholder="Any special delivery instructions..."
                      rows={3}
                      disabled={isViewMode}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="access-requirements">Access Requirements</Label>
                    <Textarea
                      id="access-requirements"
                      value={formData.access_requirements}
                      onChange={(e) => setFormData({ ...formData, access_requirements: e.target.value })}
                      placeholder="Security requirements, access codes, etc."
                      rows={3}
                      disabled={isViewMode}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="dock-info">Dock Information</Label>
                    <Textarea
                      id="dock-info"
                      value={formData.dock_information}
                      onChange={(e) => setFormData({ ...formData, dock_information: e.target.value })}
                      placeholder="Loading dock details, restrictions, etc."
                      rows={3}
                      disabled={isViewMode}
                    />
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>

          {/* Carrier Preferences */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Carrier Preferences</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="preferred-carriers">Preferred Carriers</Label>
                <p className="text-xs text-muted-foreground">Specify carriers you prefer for deliveries to this location</p>
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <Input
                      id="preferred-carriers"
                      value={newPreferredCarrier}
                      onChange={(e) => setNewPreferredCarrier(e.target.value)}
                      placeholder="Add preferred carrier (e.g., FedEx, UPS)..."
                      disabled={isViewMode}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addCarrier('preferred');
                        }
                      }}
                    />
                    <Button 
                      type="button"
                      onClick={() => addCarrier('preferred')}
                      disabled={isViewMode || !newPreferredCarrier.trim()}
                      size="icon"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  {formData.preferred_carriers.length > 0 && (
                    <div className="flex gap-2 flex-wrap">
                      {formData.preferred_carriers.map((carrier) => (
                        <Badge key={carrier} variant="secondary" className="flex items-center gap-1">
                          {carrier}
                          {!isViewMode && (
                            <X 
                              className="h-3 w-3 cursor-pointer hover:text-destructive" 
                              onClick={() => removeCarrier(carrier, 'preferred')} 
                            />
                          )}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="restricted-carriers">Restricted Carriers</Label>
                <p className="text-xs text-muted-foreground">Specify carriers that should not be used for this location</p>
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <Input
                      id="restricted-carriers"
                      value={newRestrictedCarrier}
                      onChange={(e) => setNewRestrictedCarrier(e.target.value)}
                      placeholder="Add restricted carrier..."
                      disabled={isViewMode}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addCarrier('restricted');
                        }
                      }}
                    />
                    <Button 
                      type="button"
                      onClick={() => addCarrier('restricted')}
                      disabled={isViewMode || !newRestrictedCarrier.trim()}
                      size="icon"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  {formData.restricted_carriers.length > 0 && (
                    <div className="flex gap-2 flex-wrap">
                      {formData.restricted_carriers.map((carrier) => (
                        <Badge key={carrier} variant="destructive" className="flex items-center gap-1">
                          {carrier}
                          {!isViewMode && (
                            <X 
                              className="h-3 w-3 cursor-pointer hover:text-destructive-foreground" 
                              onClick={() => removeCarrier(carrier, 'restricted')} 
                            />
                          )}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <DialogFooter className="flex items-center justify-between">
          <div className="flex gap-2">
            {isViewMode && (
              <Button type="button" onClick={() => onModeChange('edit')}>
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Button>
            )}
            {!isViewMode && (
              <>
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                <Button type="button" onClick={handleSave} disabled={saving || isValidating}>
                  {saving || isValidating ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {isValidating ? 'Validating...' : 'Saving...'}
                    </>
                  ) : (
                    isCreateMode ? 'Create Address' : 'Save Changes'
                  )}
                </Button>
              </>
            )}
            {isViewMode && (
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Close
              </Button>
            )}
          </div>
        </DialogFooter>
        
        {/* Address Suggestion Dialog */}
        {lastResult && (
          <AddressSuggestionDialog
            open={showSuggestionDialog}
            onOpenChange={setShowSuggestionDialog}
            originalAddress={{
              street: formData.address_line1,
              city: formData.city,
              state: formData.state_province,
              zipcode: formData.postal_code,
            }}
            validationResult={lastResult}
            onSelectAddress={handleSelectSuggestedAddress}
            onKeepOriginal={handleKeepOriginal}
            onEditAddress={handleEditAddress}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};
